import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { useLocalization } from '../../contexts/LocalizationContext';
import { isDemographicsComplete, isNewSignupFlow, markNewSignupFlow } from '../../utils/googleAuth';
import AnnouncementBar from '../../components/AnnouncementBar';
import Footer from '../../components/Footer';
import MorafaaLogo from '../../components/ui/MorafaaLogo';
import SignupStepBadge from '../../components/auth/SignupStepBadge';

const DemographicsSetup: React.FC = () => {
  const navigate = useNavigate();
  const { user, setUser, isLoggedIn } = useAuth();
  const { isRTL, language } = useLocalization();
  const [phoneNumber, setPhoneNumber] = useState(user?.phoneNumber || '');
  const [birthdate, setBirthdate] = useState(user?.birthdate || '');
  const [gender, setGender] = useState(user?.gender || '1');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/login', { replace: true });
      return;
    }
    if (user && isDemographicsComplete(user)) {
      if (isNewSignupFlow()) {
        navigate('/auth/profile-photo', { replace: true });
      } else {
        navigate('/loading', { replace: true });
      }
    }
  }, [isLoggedIn, user, navigate]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!phoneNumber.trim()) {
      setError(isRTL ? 'رقم الهاتف مطلوب' : 'Phone number is required');
      return;
    }
    if (!birthdate.trim()) {
      setError(isRTL ? 'تاريخ الميلاد مطلوب' : 'Birth date is required');
      return;
    }
    // Store as dd-MM-yyyy to match mobile Users contract when coming from HTML date input (yyyy-MM-dd)
    let birthOut = birthdate;
    if (/^\d{4}-\d{2}-\d{2}$/.test(birthdate)) {
      const [y, m, d] = birthdate.split('-');
      birthOut = `${d}-${m}-${y}`;
    }
    setLoading(true);
    try {
      const updated = await AuthService.updateDemographics({
        phoneNumber: phoneNumber.trim(),
        birthdate: birthOut,
        gender,
      });
      setUser(updated);
      if (isNewSignupFlow()) {
        navigate('/auth/profile-photo', { replace: true });
      } else {
        markNewSignupFlow(false);
        navigate('/loading', { replace: true });
      }
    } catch (err: any) {
      setError(err?.message || (isRTL ? 'فشل الحفظ' : 'Save failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen bg-background-light dark:bg-background-dark ${isRTL ? 'rtl' : 'ltr'}`}>
      <AnnouncementBar />
      <div className="bg-primary dark:bg-primary-dark py-4 px-4">
        <div className="max-w-md mx-auto flex items-center gap-2">
          <MorafaaLogo size="sm" variant="light" />
          <span className="text-white font-bold">{isRTL ? 'أكمل ملفك الشخصي' : 'Complete your profile'}</span>
        </div>
      </div>
      <div className="flex justify-center p-6">
        <form onSubmit={onSubmit} className="w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 space-y-4">
          {isNewSignupFlow() && <SignupStepBadge step={2} isRTL={isRTL} />}
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">
            {isRTL ? 'أكمل ملفك الشخصي' : 'Complete your profile'}
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            {isRTL
              ? 'يرجى إدخال رقم الهاتف وتاريخ الميلاد والجنس للمتابعة.'
              : 'Please enter your phone number, birth date, and gender to continue.'}
          </p>
          {error && <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}

          <div>
            <label className="block text-sm font-medium mb-1">{isRTL ? 'رقم الهاتف' : 'Phone number'} *</label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              required
              className="w-full px-3 py-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700"
              placeholder="01XXXXXXXXX"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{isRTL ? 'تاريخ الميلاد' : 'Birth date'} *</label>
            <input
              type="date"
              value={/^\d{2}-\d{2}-\d{4}$/.test(birthdate)
                ? (() => {
                    const [d, m, y] = birthdate.split('-');
                    return `${y}-${m}-${d}`;
                  })()
                : birthdate}
              onChange={(e) => setBirthdate(e.target.value)}
              required
              max={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{isRTL ? 'الجنس' : 'Gender'}</label>
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className="w-full px-3 py-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700"
            >
              <option value="1">{isRTL ? 'ذكر' : 'Male'}</option>
              <option value="2">{isRTL ? 'أنثى' : 'Female'}</option>
            </select>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-white py-3 rounded-lg font-medium disabled:opacity-50"
          >
            {loading
              ? language === 'ar' ? 'جاري الحفظ...' : 'Saving...'
              : language === 'ar' ? 'متابعة' : 'Continue'}
          </button>
        </form>
      </div>
      <Footer />
    </div>
  );
};

export default DemographicsSetup;
